import { LeftOutlined } from "@ant-design/icons"
import { Tag, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import Button from "../../../../../../Components/Button"
import DeadlinePill from "../../../../../../Components/DeadlinePill"
import { RoutesPaths } from "../../../../../../constants/user-routes"
import Icon from "../../../../../../Icon"
import Images from "../../../../../../Images"
import utils, { paymentProcessorMap } from "../../../../../../Utils/utils"
import { STATUS_TAG } from "../../constants/statusTag"
import { titleCase } from "../../utils/titleCase"

import styles from "./detailTopbar.module.less"

const { Text } = Typography

// Page header: identity + status meta on the left, the dispute's primary decision on
// the right. Both CTAs live here rather than in a tab pane so they stay visible and
// reachable from every tab.
const DetailTopbar = ({
  dispute,
  escalated,
  stageLabel,
  isActionable,
  actioning,
  submitting,
  onReject,
  onApprove,
  onReopen
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const status = dispute?.status
  const statusMeta = STATUS_TAG[status] || STATUS_TAG.needs_response
  // An inquiry is answered, not fought: the issuer is still only asking questions, so the
  // give-up action reads "Don't respond" instead of "Stop fighting" (same behaviour, the
  // case is skipped and no evidence goes out).
  const giveUpLabel = dispute?.dispute_type === "inquiry" ? t("Don't respond") : t("Stop fighting")
  // A closed dispute can only be reopened while the issuer's deadline is still open.
  const deadlinePassed = !!(dispute?.due_by && dayjs(dispute.due_by).isBefore(dayjs()))

  // Processor identity + brand logo, mirroring the All-page cell. `dispute.processor`
  // is always populated (slug: stripe / paypal / ... / manual), unlike the frequently
  // null payment_processor_name, so we render the real name + icon off it.
  const processorSlug = dispute?.processor
  const processorLabel = processorSlug
    ? paymentProcessorMap[processorSlug] || titleCase(processorSlug)
    : null
  const processorIcon = processorSlug ? Images[`integration_${processorSlug}`] : null

  return (
    <div className={styles.topbar}>
      <button
        type="button"
        className={styles.back}
        onClick={() => navigate(RoutesPaths.recover.all)}
      >
        <LeftOutlined /> <span>{t("All disputes")}</span>
      </button>
      <span className={styles.id}>{dispute.processor_dispute_id}</span>
      <Tag color={statusMeta.color}>{t(statusMeta.label)}</Tag>
      {escalated ? <Tag color="orange">{stageLabel}</Tag> : null}
      {/* Same gate as the action bar (isActionable), so a reopened cycle shows its
          deadline too: it owes a fresh response, and hiding the pill left the one
          status with live CTAs and no visible due date. */}
      {isActionable ? <DeadlinePill due_by={dispute.due_by} /> : null}
      {/* Enrichment fill level lives on the Enrichment tab label itself (pill + count),
          so it is not duplicated here. */}
      {processorLabel ? (
        <span className={styles.processor}>
          {processorIcon ? (
            <img
              src={processorIcon}
              onError={utils.addDefaultSrc}
              alt={processorLabel}
              width={20}
              height={20}
              className={styles.procIcon}
            />
          ) : null}
          <Text>{processorLabel}</Text>
        </span>
      ) : null}
      <div className={styles.actions}>
        {/* Both CTAs are live for the two actionable statuses (Open / Awaiting review),
            which is exactly what isActionable gates this block on. Only `actioning`
            disables them, so a second click cannot fire while a request is in flight.
            They used to also require a generated evidence PDF, which left both dead on
            an Open dispute. */}
        {isActionable ? (
          <>
            {/* Same shape as the Disconnect action in Configure > Integrate: outlined
                danger with the circle_x icon, so destructive secondary actions look
                alike. Size is left to match the Approve button next to it. */}
            <Button
              type="default"
              danger
              className="flex center"
              icon={<Icon type="circle_x" style={{ fontSize: 20 }} />}
              onClick={onReject}
              disabled={actioning}
              text={giveUpLabel}
            />
            <Button
              type="primary"
              onClick={onApprove}
              disabled={actioning}
              loading={actioning}
              text={submitting ? t("Submitting to issuer") : t("Approve & submit")}
            />
          </>
        ) : status === "skipped" ? (
          <Tooltip title={deadlinePassed ? t("The response deadline has passed.") : ""}>
            <span style={{ display: "block" }}>
              <Button
                type="primary"
                onClick={onReopen}
                disabled={actioning || deadlinePassed}
                loading={actioning}
                text={t("Reopen dispute")}
              />
            </span>
          </Tooltip>
        ) : null}
      </div>
    </div>
  )
}

export default DetailTopbar
